import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";
import clsx from "clsx";
import { PageRefLink } from "./PageRefLink";
import { KeyItemsTable } from "./KeyItemsTable";
import { IMPORTANCE_BORDER, IMPORTANCE_BADGE } from "./constants";
import type { BoqSectionAnalysis } from "./types";

export const BoqSectionCard = ({
  section,
  onNavigate,
}: {
  section: BoqSectionAnalysis;
  onNavigate: (file: string, page: number) => void;
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const hasDetails =
    (section.key_items && section.key_items.length > 0) ||
    (section.warnings && section.warnings.length > 0);

  return (
    <div
      className={clsx(
        "bg-white border border-gray-200 rounded-lg border-l-4 overflow-hidden",
        IMPORTANCE_BORDER[section.importance] ?? "border-l-gray-300",
      )}
    >
      <button
        type="button"
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition-colors"
        onClick={() => hasDetails && setExpanded(!expanded)}
      >
        <span className="text-xs font-mono font-medium text-gray-500 shrink-0">
          {section.section_number}
        </span>
        <span className="text-sm font-medium text-gray-900 flex-1 truncate">
          {section.section_title ||
            t("smartSurveyor.researchReport.unclassified")}
        </span>
        <span
          className={clsx(
            "text-xs font-medium px-2 py-0.5 rounded-full shrink-0",
            IMPORTANCE_BADGE[section.importance] ?? IMPORTANCE_BADGE.low,
          )}
        >
          {section.importance}
        </span>
        <span className="text-xs text-gray-500 shrink-0">
          {section.position_count} {t("smartSurveyor.researchReport.positions")}
        </span>
        {section.page_range && section.file && (
          <PageRefLink
            file={section.file}
            page={section.page_range[0]}
            label={`pp. ${section.page_range[0]}–${section.page_range[1]}`}
            onNavigate={onNavigate}
          />
        )}
        {hasDetails && (
          <span className="text-gray-400 shrink-0">
            {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        )}
      </button>
      {section.importance_reason && (
        <div className="px-4 pb-2 text-xs text-gray-500 -mt-1">
          {section.importance_reason}
        </div>
      )}
      {expanded && hasDetails && (
        <div className="px-4 pb-3 space-y-2 border-t border-gray-100 pt-3">
          {section.key_items && section.key_items.length > 0 && (
            <KeyItemsTable items={section.key_items} onNavigate={onNavigate} />
          )}
          {section.warnings && section.warnings.length > 0 && (
            <div className="space-y-1">
              {section.warnings.map((w, i) => (
                <div
                  key={i}
                  className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1"
                >
                  {w}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
